/**
 * InstanceSummaryCard — Zone 3 detail card on the Overview tab.
 *
 * Org users see: instance ID, created/started/expires, public-URL access, health.
 * Superadmins additionally see (clearly marked, yellow-shaded section):
 * private IP, EC2 link, and the OpenCode tunnel command.
 *
 * @module features/sandboxes/components/overview/InstanceSummaryCard
 */
import React from 'react';
import {
  ArrowSquareOut,
  Clock,
  Cube,
  Globe,
  Heart,
  Info,
  Lock,
  MapPin,
} from '@phosphor-icons/react';
import { MetadataItem, SettingsSection } from '@chatsdet/ui';
import type { EnvironmentInstance } from '../../../../hooks/useEnvironmentInstances';
import { HealthStatusBadge } from '../HealthStatusBadge';
import { buildTunnelCommand } from '../tunnel-command';
import { formatAbsolute, formatRelative } from './time-format';

function TunnelCommandRow({ ec2InstanceId }: { ec2InstanceId: string }) {
  const [copied, setCopied] = React.useState(false);
  const command = buildTunnelCommand(ec2InstanceId);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — visual feedback toggles only on success
    }
  };
  return (
    <div className="rounded-md bg-slate-900 text-slate-100 p-2.5 font-mono text-[12px] flex items-center gap-2">
      <span className="flex-1 truncate">{command}</span>
      <button
        type="button"
        onClick={handleCopy}
        className="text-slate-300 hover:text-white text-[11px] shrink-0"
        aria-label="Copy tunnel command"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export interface InstanceSummaryCardProps {
  instance: EnvironmentInstance;
  isSuperadmin: boolean;
}

function AccessValue({
  isDead,
  isPrivate,
  publicUrl,
}: {
  isDead: boolean;
  isPrivate: boolean;
  publicUrl: string | null | undefined;
}) {
  if (isDead) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (isPrivate) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Lock size={14} /> Private — No public URL
      </span>
    );
  }
  if (publicUrl) {
    return (
      <a
        href={publicUrl}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="font-mono text-primary hover:underline break-all"
      >
        {publicUrl}
      </a>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

export function InstanceSummaryCard({ instance, isSuperadmin }: InstanceSummaryCardProps) {
  const isDead = instance.status === 'terminated' || instance.status === 'failed';

  return (
    <SettingsSection title="Instance">
      <div className="divide-y divide-border -mt-1">
        <MetadataItem icon={<Cube size={14} />} label="Instance ID" mono>
          {instance.id}
        </MetadataItem>
        <MetadataItem icon={<Clock size={14} />} label="Created">
          {formatRelative(instance.createdAt)} ·{' '}
          <span className="text-muted-foreground">{formatAbsolute(instance.createdAt)}</span>
        </MetadataItem>
        {instance.startedAt && (
          <MetadataItem icon={<Clock size={14} />} label="Started">
            {formatRelative(instance.startedAt)}
          </MetadataItem>
        )}
        {isDead ? (
          <>
            <MetadataItem icon={<Clock size={14} />} label="Terminated">
              {instance.terminatedAt ? (
                <>
                  {formatRelative(instance.terminatedAt)} ·{' '}
                  <span className="text-muted-foreground">
                    {formatAbsolute(instance.terminatedAt)}
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </MetadataItem>
            {instance.terminationReason ? (
              <MetadataItem icon={<Info size={14} />} label="Reason">
                <span className="text-muted-foreground">{instance.terminationReason}</span>
              </MetadataItem>
            ) : null}
          </>
        ) : (
          <MetadataItem icon={<Clock size={14} />} label="Expires">
            {formatAbsolute(instance.expiresAt)}
          </MetadataItem>
        )}
        <MetadataItem icon={<Globe size={14} />} label="Access">
          <AccessValue
            isDead={isDead}
            isPrivate={instance.isPrivate}
            publicUrl={instance.publicUrl}
          />
        </MetadataItem>
        <MetadataItem icon={<Heart size={14} />} label="Health">
          <HealthStatusBadge status={instance.healthStatus} />
        </MetadataItem>
      </div>

      {isSuperadmin && (
        <SuperadminSection
          privateIp={instance.privateIp ?? null}
          ec2InstanceId={instance.instanceId ?? null}
          isRunning={instance.status === 'running'}
        />
      )}
    </SettingsSection>
  );
}

InstanceSummaryCard.displayName = 'InstanceSummaryCard';

function SuperadminSection({
  privateIp,
  ec2InstanceId,
  isRunning,
}: {
  privateIp: string | null;
  ec2InstanceId: string | null;
  isRunning: boolean;
}) {
  return (
    <div className="mt-4 pt-4 border-t border-border">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
          Superadmin only
        </span>
      </div>
      <div className="divide-y divide-border">
        {privateIp && (
          <MetadataItem icon={<MapPin size={14} />} label="Private IP" mono>
            {privateIp}
          </MetadataItem>
        )}
        <MetadataItem icon={<Cube size={14} />} label="EC2 instance">
          {ec2InstanceId ? (
            <a
              href={`https://us-east-1.console.aws.amazon.com/ec2/home?region=us-east-1#InstanceDetails:instanceId=${ec2InstanceId}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
            >
              {ec2InstanceId}
              <ArrowSquareOut size={12} />
            </a>
          ) : (
            <a
              href="https://us-east-1.console.aws.amazon.com/ec2/home?region=us-east-1#Instances:"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-1 italic text-muted-foreground hover:text-primary hover:underline"
            >
              Not launched yet → EC2 console
              <ArrowSquareOut size={12} />
            </a>
          )}
        </MetadataItem>
      </div>
      {isRunning && ec2InstanceId ? (
        <div className="pt-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
            OpenCode debug tunnel
          </div>
          <TunnelCommandRow ec2InstanceId={ec2InstanceId} />
        </div>
      ) : null}
    </div>
  );
}
